import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, sipOrder, getSchemes, getMandateStatus } from "../../../api/bseApi";

const FREQUENCY_TYPES = [
  { value: "MONTHLY", label: "MONTHLY" },
  { value: "QUARTERLY", label: "QUARTERLY" },
  { value: "WEEKLY", label: "WEEKLY" },
];
const FIRST_ORDER_FLAGS = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];
const EUIN_DECLARATION = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
];
const DP_MODES = [
  { value: "P", label: "Physical (P)" },
  { value: "C", label: "CDSL (C)" },
  { value: "N", label: "NSDL (N)" },
];

function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * SipRegistrationScreen - BSE StAR MF SIP Order Entry
 * Opens only if ClientCode exists
 * Receives: ClientCode, EUIN, Available Mandates, Scheme master list
 */
export default function SipRegistrationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [form, setForm] = useState({
    schemeCd: "",
    schemeCdManual: "",
    startDate: "",
    frequencyType: "MONTHLY",
    installmentAmount: "",
    noOfInstallments: "",
    mandateId: "",
    mandateIdManual: "",
    firstOrderFlag: "N",
    euin: "",
    euinVal: "N",
    transMode: "D",
    dpTransactionMode: "P",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const state = location.state;

    if (state?.clientCode) {
      setContext({
        clientCode: state.clientCode,
        userId: state.userId || null,
        euin: state.euin || "",
      });
      setForm((f) => ({ ...f, euin: state.euin || f.euin }));
      setFetching(false);
      return;
    }

    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid) {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({
            clientCode: String(ucc).trim(),
            userId: user.id,
            euin: "",
          });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    Swal.fire("Error", "Missing ClientCode. Open from User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  useEffect(() => {
    if (!context?.clientCode) return;
    const loadMandates = async () => {
      try {
        const fromDate = "01/01/2020";
        const toDate = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "/");
        const res = await getMandateStatus({
          fromDate,
          toDate,
          clientCode: context.clientCode,
        });
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setMandates(res.data.data);
        }
      } catch (err) {
        console.warn("Mandate list load failed:", err.message);
      }
    };
    loadMandates();
  }, [context?.clientCode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    const schemeCd = form.schemeCd || form.schemeCdManual;
    if (!schemeCd?.trim()) err.schemeCd = "Scheme selection is mandatory";

    const startDate = new Date(form.startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!form.startDate) err.startDate = "Start Date is required";
    else if (startDate < today) err.startDate = "Start Date must be a future date";

    const amt = parseFloat(form.installmentAmount);
    if (!form.installmentAmount || amt <= 0) err.installmentAmount = "Installment Amount must be > 0";

    const installments = parseInt(form.noOfInstallments, 10);
    if (!form.noOfInstallments || installments <= 0) err.noOfInstallments = "Number of Installments must be > 0";

    const mandateId = form.mandateId || form.mandateIdManual;
    if (!mandateId?.trim()) err.mandateId = "Mandate selection is mandatory";

    if (!form.euin?.trim()) err.euin = "EUIN is mandatory";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const schemeCd = (form.schemeCd || form.schemeCdManual || "").trim();
      const mandateId = (form.mandateId || form.mandateIdManual || "").trim();
      const startDateFormatted = formatDateForBse(form.startDate);

      const schemeName = form.schemeCdManual ? "" : (schemes.find((s) => s.schemeCd === schemeCd)?.schemeName || "");
      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        userId: context.userId || undefined,
        schemeCd,
        schemeName,
        startDate: startDateFormatted,
        frequencyType: form.frequencyType,
        frequencyAllowed: 1,
        installmentAmount: parseFloat(form.installmentAmount),
        noOfInstallments: parseInt(form.noOfInstallments, 10),
        mandateId,
        firstOrderFlag: form.firstOrderFlag,
        euin: form.euin.trim(),
        euinVal: form.euinVal,
        transMode: form.transMode,
        dpTransactionMode: form.dpTransactionMode,
        dpc: "Y",
      };

      const res = await sipOrder(payload);

      if (res.data?.status === 1) {
        const sipRegId = res.data?.data?.sipRegId;
        const firstAmount = parseFloat(form.installmentAmount);
        Swal.fire({
          icon: "success",
          title: "SIP Registered",
          text: `SIP registered successfully. SIP Reg ID: ${sipRegId || "N/A"}`,
        });
        navigate("/payment/mode", {
          state: { orderNo: sipRegId, clientCode: context.clientCode, amount: firstAmount },
        });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "SIP registration failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "SIP registration failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;
  const useManualMandate = mandates.length === 0;

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item active">SIP Registration</li>
              </ol>
            </nav>
            <h5>SIP Registration</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                SIP Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Scheme *</label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCdManual"
                      value={form.schemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter BSE Scheme Code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.schemeCd ? "is-invalid" : ""}`}
                      name="schemeCd"
                      value={form.schemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.schemeCd && <div className="invalid-feedback">{errors.schemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Start Date (DD/MM/YYYY) *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                  />
                  {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Frequency (FREQUENCYTYPE)</label>
                  <select
                    className="form-select"
                    name="frequencyType"
                    value={form.frequencyType}
                    onChange={handleChange}
                  >
                    {FREQUENCY_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Installment Amount *</label>
                  <input
                    type="number"
                    className={`form-control ${errors.installmentAmount ? "is-invalid" : ""}`}
                    name="installmentAmount"
                    value={form.installmentAmount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    min="1"
                    step="0.01"
                  />
                  {errors.installmentAmount && <div className="invalid-feedback">{errors.installmentAmount}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Number of Installments *</label>
                  <input
                    type="number"
                    className={`form-control ${errors.noOfInstallments ? "is-invalid" : ""}`}
                    name="noOfInstallments"
                    value={form.noOfInstallments}
                    onChange={handleChange}
                    placeholder="e.g. 12"
                    min="1"
                    step="1"
                  />
                  {errors.noOfInstallments && <div className="invalid-feedback">{errors.noOfInstallments}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Mandate (MANDATEID) *</label>
                  {useManualMandate ? (
                    <>
                      <input
                        type="text"
                        className={`form-control ${errors.mandateId ? "is-invalid" : ""}`}
                        name="mandateIdManual"
                        value={form.mandateIdManual}
                        onChange={handleChange}
                        placeholder="Enter Mandate ID"
                      />
                      <small className="text-muted">
                        <Link to="/user/mandate-registration" state={{ clientCode: context?.clientCode }}>Register a mandate first</Link>
                      </small>
                    </>
                  ) : (
                    <select
                      className={`form-select ${errors.mandateId ? "is-invalid" : ""}`}
                      name="mandateId"
                      value={form.mandateId}
                      onChange={handleChange}
                    >
                      <option value="">Select Mandate</option>
                      {mandates.map((m, i) => {
                        const mid = typeof m === "object" ? (m.MandateId || m.mandateId || m.RegID || m.RegnNo || "") : String(m);
                        const mlabel = typeof m === "object" ? (m.MandateType || m.MandateId || mid || `Mandate ${i + 1}`) : String(m);
                        return (
                          <option key={i} value={mid}>
                            {mlabel}
                          </option>
                        );
                      })}
                    </select>
                  )}
                  {errors.mandateId && <div className="invalid-feedback">{errors.mandateId}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">First Order Today (FIRSTORDERFLAG)</label>
                  <select
                    className="form-select"
                    name="firstOrderFlag"
                    value={form.firstOrderFlag}
                    onChange={handleChange}
                  >
                    {FIRST_ORDER_FLAGS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">EUIN *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.euin ? "is-invalid" : ""}`}
                    name="euin"
                    value={form.euin}
                    onChange={handleChange}
                    placeholder="Enter EUIN"
                  />
                  {errors.euin && <div className="invalid-feedback">{errors.euin}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">EUIN Declaration</label>
                  <select
                    className="form-select"
                    name="euinVal"
                    value={form.euinVal}
                    onChange={handleChange}
                  >
                    {EUIN_DECLARATION.map((e) => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">DP Transaction Mode</label>
                  <select
                    className="form-select"
                    name="dpTransactionMode"
                    value={form.dpTransactionMode}
                    onChange={handleChange}
                  >
                    {DP_MODES.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Registering SIP...
                    </>
                  ) : (
                    "Register SIP"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
